// src/backendServices/productManagment/api_productCategory.ts

import   axios                         from 'axios';

import { API_URLS                   }  from '../urls';

import { BackendResponse , handleError           ,
         DUPLICATE_NAME_CODE , isDuplicateNameRefusal ,
         extractApiErrorDetail }  from '../../dataTypes/backendServicesManagment/types'

import { ProductCategory            ,
         initializedProductCategory }  from '../../dataTypes/productManagment/types'

export interface BackendResponse_ProductCategory extends BackendResponse<ProductCategory []> {}


export const getProductCategories = async (adminId?: number): Promise<BackendResponse_ProductCategory> => {

  const url         = API_URLS.getProductCategories;
  const queryParams = new URLSearchParams();
  if (adminId) queryParams.append('adminId', adminId.toString());
  const queryString = queryParams.toString();
  const fullUrl      = queryString ? `${url}?${queryString}` : url;

  try
   {
    const response = await axios.get(fullUrl);
	
    if (response.status === 200) 
	      {
           const categories: ProductCategory[] = response.data['hydra:member'].map((category: any) => {
	  
             const defaultCategory = initializedProductCategory();
		
             return {
                ...defaultCategory,
                id      : category.id      ?? defaultCategory.id      ,
                name    : category.name    ?? defaultCategory.name    ,
                rank    : category.rank    ?? defaultCategory.rank    ,
                visible : category.visible ?? defaultCategory.visible ,
             };
           });

           return {
             code    : 1,
             comment : "The retrieval of video categories is complete.",
             payload : categories
           };
          } 
	 else {
           return handleError<ProductCategory []>(response.status, "Non-200 response from API");
          } 
   } 
  catch (error) 
   {
    if (axios.isAxiosError(error)) 
	 {
      return handleError<ProductCategory []>(-101, error.response?.data?.detail ?? error.message);
     }

    return handleError<ProductCategory []>(-102, "Unknown error");
   }
};

export const createProductCategory = async (name: string, rank: number, visible: boolean, adminId?: number): Promise<BackendResponse<ProductCategory>> => {
  const url = API_URLS.createProductCategory;

  const headers = {
    'Accept': 'application/ld+json',
    'Content-Type': 'application/merge-patch+json'
  };

  const rankStr:    string = `${rank}`;
  const visibleStr: string = visible ? 'true' : 'false';

  try {
    const response = await axios.post(url, { name, rankStr, visibleStr, ...(adminId ? { adminId } : {}) }, { headers });

    if (response.status === 201) {
      const defaultCategory = initializedProductCategory();
      const created: ProductCategory = {
        ...defaultCategory,
        id      : response.data?.id      ?? defaultCategory.id      ,
        name    : response.data?.name    ?? defaultCategory.name    ,
        rank    : response.data?.rank    ?? defaultCategory.rank    ,
        visible : response.data?.visible ?? defaultCategory.visible ,
      };
      return {
        code: 1,
        comment: "A new product category has been successfully created.",
        payload : created
      };
    } else {
      return handleError<ProductCategory>(response.status, "Non-201 response from API");
    }
  } catch (error) {

    if (axios.isAxiosError(error)) {
      const detail = extractApiErrorDetail(error.response?.data, error.message);

      // Un nom déjà pris n'est pas une panne : l'écran a de quoi le dire à
      // l'utilisateur, à condition de pouvoir le distinguer du reste.
      if (isDuplicateNameRefusal(error.response?.status, detail)) {
        return handleError<ProductCategory>(DUPLICATE_NAME_CODE, detail);
      }

      return handleError<ProductCategory>(-101, detail);
    }
    return handleError<ProductCategory>(-102, "Unknown error");
  }
};

export const patchProductCategory = async (id: number, name: string, rank: number, visible: boolean, adminId?: number): Promise<BackendResponse> => {
  const baseUrl = `${API_URLS.patchProductCategory}`;
  const url = baseUrl.replace("{id}", `${id}`);

  const headers = {
    'Accept': 'application/ld+json',
    'Content-Type': 'application/merge-patch+json'
  };

  try {
    const response = await axios.patch(url, { name, rank, visible, ...(adminId ? { adminId } : {}) }, { headers });

    if (response.status === 200) {
      return {
        code: 1,
        comment: `The update of product category ${id} is complete.`,
        payload : {} as any
      };
    } else {
      return handleError<void>(response.status, "Non-200 response from API");
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const detail = extractApiErrorDetail(error.response?.data, error.message);

      // Même refus qu'à la création, autre porte : renommer vers un nom déjà pris.
      if (isDuplicateNameRefusal(error.response?.status, detail)) {
        return handleError<void>(DUPLICATE_NAME_CODE, detail);
      }

      return handleError<void>(-101, detail);
    }
    return handleError<void>(-102, "Unknown error");
  }
};

export const deleteProductCategory = async (id: number, adminId?: number): Promise<BackendResponse> => {
  const baseUrl      = `${API_URLS.deleteProductCategory}`;
  const queryParams  = new URLSearchParams();
  if (adminId) queryParams.append('adminId', adminId.toString());
  const queryString  = queryParams.toString();
  const url          = baseUrl.replace("{id}", `${id}`) + (queryString ? `?${queryString}` : '');

  const headers = {
    'Accept': 'application/ld+json'
  };

  try {
    const response = await axios.delete(url, { headers });

    if (response.status === 204) {
      return {
        code: 1,
        comment: `The deletion of product category ${id} is complete.`,
        payload : {} as any
      };
    } else {
      return handleError<void>(response.status, "Non-204 response from API");
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, "Unknown error");
  }
};

